import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Swal from "../../../utils/apparifySwal";
import { businessSwalBase } from "../../utils/businessSwal";
import { businessProductsAPI } from "../../services/businessProductsService";

function prettyAccept(accept) {
  if (!accept || accept === "image/*") return "Images";
  return accept.replaceAll(",", ", ");
}

function bytesToMB(bytes) {
  return bytes / (1024 * 1024);
}

export default function ImageDropzone({
  onUploaded, // (urls: string[]) => void
  meta, // { type: "product" | "variant", variantKey?: string, productId?: string }
  multiple = true,
  accept = "image/*",
  maxFiles = 6,
  label = "Drag & drop images here, or click to upload",
  maxSizeMB = 5,
}) {
  const inputRef = useRef(null);

  const [uploading, setUploading] = useState(false);
  const uploadingRef = useRef(false);

  const [dragActive, setDragActive] = useState(false);

  // best-effort progress (file-by-file)
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  // keep ref in sync so callbacks don't depend on `uploading`
  useEffect(() => {
    uploadingRef.current = uploading;
  }, [uploading]);

  const helperText = useMemo(() => {
    const mode = multiple ? `Up to ${maxFiles} file(s)` : "Single file";
    return `${mode} • ${prettyAccept(accept)} • ≤ ${maxSizeMB}MB each`;
  }, [multiple, maxFiles, accept, maxSizeMB]);

  const validateFiles = useCallback(
    async (files) => {
      const list = Array.from(files || []);
      if (!list.length) return { ok: false, files: [] };

      const sliced = list.slice(0, multiple ? maxFiles : 1);

      // Type validation
      const acceptsAllImages = accept === "image/*";
      const acceptList = accept
        .split(",")
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);

      const badType = sliced.find((f) => {
        const mime = String(f.type || "").toLowerCase();
        if (acceptsAllImages) return !mime.startsWith("image/");
        return !acceptList.includes(mime);
      });

      if (badType) {
        await Swal.fire({
          ...businessSwalBase,
          icon: "warning",
          title: "Unsupported file",
          html: `Please upload: <b>${prettyAccept(accept)}</b>`,
          confirmButtonText: "OK",
        });
        return { ok: false, files: [] };
      }

      // Size validation
      const tooBig = sliced.find((f) => bytesToMB(f.size || 0) > Number(maxSizeMB || 0));
      if (tooBig) {
        await Swal.fire({
          ...businessSwalBase,
          icon: "warning",
          title: "File too large",
          html: `Max size is <b>${maxSizeMB}MB</b> per file.`,
          confirmButtonText: "OK",
        });
        return { ok: false, files: [] };
      }

      return { ok: true, files: sliced };
    },
    [accept, maxFiles, maxSizeMB, multiple]
  );

  const uploadFiles = useCallback(
    async (fileList) => {
      if (uploadingRef.current) return;

      const { ok, files } = await validateFiles(fileList);
      if (!ok || !files.length) return;

      setUploading(true);
      setProgress({ done: 0, total: files.length });

      try {
        const urls = [];

        for (let i = 0; i < files.length; i++) {
          const file = files[i];

          // ✅ Matches your service: uploadImage(file, meta)
          const res = await businessProductsAPI.uploadImage(file, meta);

          const url = res?.url || res?.imageUrl || res?.data?.url || res?.data?.imageUrl;
          if (url) urls.push(url);

          setProgress({ done: i + 1, total: files.length });
        }

        if (urls.length) onUploaded(urls);
      } catch (e) {
        const msg =
          e?.response?.data?.message ||
          e?.message ||
          "Image upload failed. Check backend upload endpoint.";

        await Swal.fire({
          ...businessSwalBase,
          icon: "error",
          title: "Upload failed",
          html: msg,
          confirmButtonText: "OK",
        });
      } finally {
        setUploading(false);
        setDragActive(false);
        setTimeout(() => setProgress({ done: 0, total: 0 }), 250);
      }
    },
    [meta, onUploaded, validateFiles]
  );

  const onDrop = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      await uploadFiles(e.dataTransfer.files);
    },
    [uploadFiles]
  );

  const onPick = useCallback(
    async (e) => {
      await uploadFiles(e.target.files);
      e.target.value = "";
    },
    [uploadFiles]
  );

  const openPicker = useCallback(() => {
    if (uploadingRef.current) return;
    inputRef.current?.click();
  }, []);

  const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={onPick}
        disabled={uploading}
      />

      <button
        type="button"
        onClick={openPicker}
        onDragOver={(e) => {
          e.preventDefault();
          if (!uploadingRef.current) setDragActive(true);
        }}
        onDragEnter={(e) => {
          e.preventDefault();
          if (!uploadingRef.current) setDragActive(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setDragActive(false);
        }}
        onDrop={onDrop}
        disabled={uploading}
        className={
          "w-full rounded-2xl border-2 border-dashed px-4 py-10 text-center text-xs shadow-sm transition " +
          "bg-white text-zinc-600 border-zinc-300 hover:bg-zinc-50 " +
          "dark:bg-[#121214] dark:text-zinc-400 dark:border-zinc-700 dark:hover:bg-zinc-900/40 " +
          (dragActive ? " ring-2 ring-zinc-200 dark:ring-zinc-800 border-zinc-400 dark:border-zinc-600" : "") +
          (uploading ? " cursor-not-allowed opacity-80" : "")
        }
      >
        <div className="font-medium text-zinc-900 dark:text-zinc-100">
          {uploading
            ? `Uploading ${progress.done}/${progress.total}…`
            : dragActive
              ? "Drop to upload"
              : label}
        </div>

        <div className="mt-1 text-[11px] opacity-80">{helperText}</div>

        {uploading && progress.total > 0 && (
          <div className="mt-4">
            <div className="h-2 w-full overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-800">
              <div
                className="h-full rounded-full bg-zinc-900 dark:bg-zinc-100 transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="mt-1 text-[11px] text-zinc-500 dark:text-zinc-400">{pct}%</div>
          </div>
        )}
      </button>
    </div>
  );
}
